/** A grid over the horizontal plane, aligned with the x and z axes */
export type Grid = {
  /** Size of a cell, in meters */
  cellSize: number;
  /** Position of the corner of the first cell */
  minX: number;
  minZ: number;
  width: number;
  height: number;
};

/** Heights grouped by cell: the heights of cell c are heights[starts[c]] to heights[starts[c + 1]], sorted */
export type CellHeights = { starts: Int32Array; heights: Float32Array };

/** Larger grids would not fit the memory of a browser tab */
const MAX_GRID_CELLS = 4_000_000;
/** Points of a cell closer than this in height belong to the same surface */
const LAYER_GAP = 0.06;
/** Thicker point groups are vertical surfaces or clutter */
const MAX_LAYER_THICKNESS = 0.12;
/** Height difference to a neighbor cell on the same surface, about 14° for 20 cm cells */
const SURFACE_SLOPE_TOLERANCE = 0.05;
/** A horizontal surface continues in this many of the 8 neighbor cells, walls only in 2 */
const MIN_SURFACE_NEIGHBORS = 3;

/**
 * @param points Point positions as x, y, z triplets
 * @param cellSize Preferred cell size, increased for very large point clouds
 * @returns a grid covering the points
 */
export function createGrid(points: Float32Array, cellSize: number): Grid {
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxZ = -Infinity;
  for (let i = 0; i < points.length; i += 3) {
    minX = Math.min(minX, points[i]);
    maxX = Math.max(maxX, points[i]);
    minZ = Math.min(minZ, points[i + 2]);
    maxZ = Math.max(maxZ, points[i + 2]);
  }
  const size = Math.max(
    cellSize,
    Math.sqrt(((maxX - minX) * (maxZ - minZ)) / MAX_GRID_CELLS),
  );
  return {
    cellSize: size,
    minX,
    minZ,
    width: Math.floor((maxX - minX) / size) + 1,
    height: Math.floor((maxZ - minZ) / size) + 1,
  };
}

/**
 * @param points Point positions as x, y, z triplets
 * @param grid The grid
 * @returns the point heights sorted by cell
 */
export function groupHeights(points: Float32Array, grid: Grid): CellHeights {
  const cellCount = grid.width * grid.height;
  const count = points.length / 3;
  const cells = new Int32Array(count);
  const starts = new Int32Array(cellCount + 1);
  for (let i = 0; i < count; i++) {
    const column = Math.floor((points[i * 3] - grid.minX) / grid.cellSize);
    const row = Math.floor((points[i * 3 + 2] - grid.minZ) / grid.cellSize);
    cells[i] = row * grid.width + column;
    starts[cells[i] + 1]++;
  }
  for (let cell = 0; cell < cellCount; cell++) {
    starts[cell + 1] += starts[cell];
  }
  const next = starts.slice(0, cellCount);
  const heights = new Float32Array(count);
  for (let i = 0; i < count; i++) heights[next[cells[i]]++] = points[i * 3 + 1];
  for (let cell = 0; cell < cellCount; cell++) {
    if (starts[cell + 1] - starts[cell] > 1) {
      heights.subarray(starts[cell], starts[cell + 1]).sort();
    }
  }
  return { starts, heights };
}

/**
 * @param cells The point heights by cell
 * @returns the heights of the thin groups of points of each cell, candidate horizontal surfaces
 */
export function findLayers({ starts, heights }: CellHeights): CellHeights {
  const cellCount = starts.length - 1;
  const layerStarts = new Int32Array(cellCount + 1);
  const layers: number[] = [];
  for (let cell = 0; cell < cellCount; cell++) {
    layerStarts[cell] = layers.length;
    const end = starts[cell + 1];
    let first = starts[cell];
    for (let i = first + 1; i <= end; i++) {
      if (i < end && heights[i] - heights[i - 1] <= LAYER_GAP) continue;
      if (heights[i - 1] - heights[first] <= MAX_LAYER_THICKNESS) {
        let sum = 0;
        for (let k = first; k < i; k++) sum += heights[k];
        layers.push(sum / (i - first));
      }
      first = i;
    }
  }
  layerStarts[cellCount] = layers.length;
  return { starts: layerStarts, heights: Float32Array.from(layers) };
}

/**
 * @param grid The grid
 * @param layers The candidate surfaces of each cell
 * @returns for each layer, 1 if it continues in enough neighbor cells to be a horizontal surface
 */
export function markHorizontal(
  { width, height }: Grid,
  { starts, heights }: CellHeights,
): Uint8Array {
  const isHorizontal = new Uint8Array(heights.length);
  for (let row = 0; row < height; row++) {
    for (let column = 0; column < width; column++) {
      const cell = row * width + column;
      for (let layer = starts[cell]; layer < starts[cell + 1]; layer++) {
        let neighbors = 0;
        for (let dr = -1; dr <= 1; dr++) {
          for (let dc = -1; dc <= 1; dc++) {
            const r = row + dr;
            const c = column + dc;
            if ((!dr && !dc) || r < 0 || c < 0 || r >= height || c >= width) {
              continue;
            }
            const other = r * width + c;
            for (let k = starts[other]; k < starts[other + 1]; k++) {
              if (
                Math.abs(heights[k] - heights[layer]) <= SURFACE_SLOPE_TOLERANCE
              ) {
                neighbors++;
                break;
              }
            }
          }
        }
        if (neighbors >= MIN_SURFACE_NEIGHBORS) isHorizontal[layer] = 1;
      }
    }
  }
  return isHorizontal;
}
